// ─────────────────────────────────────────────────────────────────────────────
// TopBar Component - TypeScript Implementation
// Application header with status indicators and user information
// ─────────────────────────────────────────────────────────────────────────────

import React, { useEffect, useState } from "react";
import "./TopBar.css";
import useOnlineStatus from "../../hooks/useOnlineStatus";
import { useAuthActions, useAuthState } from "../../hooks/providers/AuthProvider";
import { getSessionTimeRemaining } from "../../utilis/sessionTimeCalculator";
import LogoutConfirmationModal from "../Modals/LogoutConfirmationModal";
// Props interface for TopBar component
interface TopBarProps {
  currentPatient?: {
    id: string;
    name: string;
  } | null;
  sessionDuration?: string;
  environment?: "development" | "staging" | "production";
  className?: string;
}

// Status item interface
interface StatusItem {
  label?: string;
  value?: string;
  icon?: string;
  status?: "online" | "offline" | "checking" | "normal";
}

const TopBar: React.FC<TopBarProps> = ({
  currentPatient = null,
  sessionDuration = "29d",
  environment = "development",
  className = "",
}) => {
  const { online, checking } = useOnlineStatus();
  const authActions = useAuthActions();
  const authState = useAuthState();
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  
  // Update current time every minute to refresh session countdown
  useEffect(() => {
    const interval = setInterval(() => {
      // Just trigger a re-render to update the session time
      // The utility function will calculate current time internally
    }, 60000); // Update every minute
    
    return () => clearInterval(interval);
  }, []);
  
  // Get user email from auth state
  const userEmail = authState?.userProfile?.email || "user@example.com";
  const sessionExpireAt = authState?.token?.tokenExpiration;
  
  // Get user name for modal
  const userName = authState?.userProfile?.firstName && authState?.userProfile?.lastName
    ? `${authState.userProfile.firstName} ${authState.userProfile.lastName}`
    : authState?.userProfile?.email || "User";

  const handleLogout = () => {
    setShowLogoutModal(true);
  };

  const handleLogoutConfirm = () => {
    setShowLogoutModal(false);
    authActions.handleLogoutActionClick();
  };

  const handleLogoutCancel = () => {
    setShowLogoutModal(false);
  };

  // Helper function to get status text and styling
  const getConnectionStatus = (): StatusItem => {
    if (checking) {
      return {
        status: "checking",
        value: "Checking...",
        icon: "⟳",
      };
    }
    return {
      status: online ? "online" : "offline",
      value: online ? "Online" : "Offline",
      icon: online ? "●" : "●",
    };
  };

  const connectionStatus = getConnectionStatus();

  // Helper function to format patient display
  const getPatientDisplay = (): string => {
    if (!currentPatient) return "None Selected";
    return currentPatient.name;
  };

  // Helper function to get environment styling
  const getEnvironmentClass = (): string => {
    switch (environment) {
      case "production":
        return "production";
      case "staging":
        return "staging";
      case "development":
      default:
        return "development";
    }
  };

  return (
    <div className={`topbar-container ${className}`}>
      <div className="topbar-status-section">
        <div
          className={`topbar-status-item ${connectionStatus.status}`}
          title={
            checking
              ? "Checking network connection..."
              : `App Status: ${connectionStatus.value}`
          }
        >
          <span
            className={`topbar-status-dot ${online ? "topbar-dot-online" : "topbar-dot-offline"}`}
            aria-label={connectionStatus.value}
          />
          <span className="topbar-status-text">
            App Status: {connectionStatus.value}
          </span>
        </div>

        <div className="topbar-status-item topbar-patient-info">
          <span className="topbar-status-label">Current Patient:</span>
          <span
            className="topbar-status-value"
            title={
              currentPatient
                ? `Patient ID: ${currentPatient.id}`
                : "No patient selected"
            }
          >
            {getPatientDisplay()}
          </span>
        </div>
      </div>

      <div className="topbar-info-section">
        <div className={`topbar-status-item ${getEnvironmentClass()}`}>
          <span
            className="topbar-status-dot"
            aria-label={`Environment: ${process.env.REACT_APP_ENVIRONMENT}`}
          />
          <span className="topbar-status-text">
            {process.env.REACT_APP_ENVIRONMENT ? process.env.REACT_APP_ENVIRONMENT.charAt(0).toUpperCase() + process.env.REACT_APP_ENVIRONMENT.slice(1) : "Unknown"} {process.env.REACT_APP_VERSION ? `v${process.env.REACT_APP_VERSION}` : "v18.1.0"}
          </span>
        </div>

        <div className="topbar-status-item topbar-session">
          <span
            className="topbar-status-icon"
            role="img"
            aria-label="Session duration"
          >
            ⏰
          </span>
          <span
            className="topbar-status-text"
            title={sessionExpireAt ? `Session expires at: ${new Date(sessionExpireAt).toLocaleString()}` : "Session expiration unknown"}
          >
            Session: {getSessionTimeRemaining(sessionExpireAt)}
          </span>
        </div>

        <div className="topbar-status-item topbar-user">
          <span className="topbar-status-icon" role="img" aria-label="User account">
            👤
          </span>
          <span className="topbar-status-text" title={`Logged in as: ${userEmail}`}>
            {userEmail}
          </span>
          <button 
            className="topbar-logout-button"
            onClick={handleLogout}
            title="Logout"
          >
            🚪
          </button>
        </div>
      </div>

      <LogoutConfirmationModal
        isOpen={showLogoutModal}
        onConfirm={handleLogoutConfirm}
        onCancel={handleLogoutCancel}
        userName={userName}
      />
    </div>
  );
};

export default TopBar;
export type { TopBarProps, StatusItem };
